import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import FixedSidebar from '../../components/ui/FixedSidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import authService from '../../services/authService';
import jobService from '../../services/jobService';
import { hasActionAccess } from '../../utils/roleBasedAccess';
import ViewJobDialog from '../../components/job-management/ViewJobDialog';
import EditJobDialog from '../../components/job-management/EditJobDialog';
import DeleteJobDialog from '../../components/job-management/DeleteJobDialog';

const JobManagementPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState(authService.getStoredUser());
  const [jobs, setJobs] = useState([]);
  const [allJobs, setAllJobs] = useState([]); // Store all jobs for client-side filtering
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({
    status: '',
    department: '',
    search: '',
    employment_type: '',
    experience_level: '',
    priority: '',
    location: '',
    remote_work_allowed: '',
    salary_min: '',
    salary_max: '',
    date_from: '',
    date_to: ''
  });
  const [sortConfig, setSortConfig] = useState({
    field: 'created_at',
    direction: 'desc'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total: 0,
    total_pages: 0
  });

  // Debounced search to improve performance
  const [searchInput, setSearchInput] = useState('');
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);

  // Handle search input with debouncing
  const handleSearchChange = (value) => {
    setSearchInput(value);
    
    // Clear existing timer
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }
    
    // Set new timer
    const timer = setTimeout(() => {
      handleFilterChange('search', value);
    }, 500); // 500ms delay
    
    setSearchDebounceTimer(timer);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }
    };
  }, [searchDebounceTimer]);

  // Format salary for display
  const formatSalary = (min, max, currency = 'USD') => {
    if (!min && !max) return 'Not specified';
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    
    if (min && max) {
      return `${formatter.format(min)} - ${formatter.format(max)}`;
    } else if (min) {
      return `${formatter.format(min)}+`;
    } else {
      return `Up to ${formatter.format(max)}`;
    }
  };
  const [viewDialog, setViewDialog] = useState({ isOpen: false, jobId: null });
  const [editDialog, setEditDialog] = useState({ isOpen: false, job: null });
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, job: null });

  // Check permissions
  const canViewJobs = hasActionAccess(currentUser?.role, 'jobs', 'view-jobs');
  const canCreateJobs = hasActionAccess(currentUser?.role, 'jobs', 'create-jobs');
  const canManageJobs = hasActionAccess(currentUser?.role, 'jobs', 'manage-jobs');
  const canDeleteJobs = hasActionAccess(currentUser?.role, 'jobs', 'delete-jobs');

  useEffect(() => {
    if (canViewJobs) {
      loadJobs();
      loadStats();
    }
  }, [filters, pagination.page, sortConfig, canViewJobs]);

  // Show success message if coming from create page
  useEffect(() => {
    if (location.state?.message) {
      // You could show a toast notification here
      console.log(location.state.message);
    }
  }, [location.state]);

  const loadJobs = async () => {
    try {
      setLoading(true);
      
      // Prepare filters object, removing empty values
      const activeFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value && value.toString().trim() !== '')
      );
      
      // Add sort parameters
      const requestParams = {
        ...activeFilters,
        sort_by: sortConfig.field,
        sort_order: sortConfig.direction
      };
      
      // Enhanced debugging
      console.log('[JobManagement] Loading jobs with params:', {
        activeFilters,
        requestParams,
        page: pagination.page,
        pageSize: pagination.page_size
      });
      
      const data = await jobService.getJobs(requestParams, pagination.page, pagination.page_size);
      
      console.log('[JobManagement] Jobs response received:', {
        jobsCount: data.jobs?.length,
        total: data.total,
        filtersApplied: Object.keys(activeFilters).length > 0,
        firstJob: data.jobs?.[0]
      });
      
      // If no server-side filtering worked, apply client-side filtering
      let finalJobs = data.jobs || [];
      
      // Store all jobs for client-side filtering fallback
      if (Object.keys(activeFilters).length === 0) {
        setAllJobs(finalJobs);
      }
      
      // Apply client-side filtering if we have filters but got same data
      if (Object.keys(activeFilters).length > 0 && allJobs.length > 0) {
        finalJobs = applyClientSideFilters(allJobs, activeFilters);
        console.log('[JobManagement] Applied client-side filtering:', {
          originalCount: allJobs.length,
          filteredCount: finalJobs.length,
          filters: activeFilters
        });
      }
      
      setJobs(finalJobs);
      setPagination(prev => ({
        ...prev,
        total: data.total || finalJobs.length,
        total_pages: data.total_pages || Math.ceil(finalJobs.length / pagination.page_size)
      }));
    } catch (error) {
      console.error('Error loading jobs:', error);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  // Client-side filtering fallback
  const applyClientSideFilters = (jobsList, activeFilters) => {
    return jobsList.filter(job => {
      // Search filter
      if (activeFilters.search) {
        const searchTerm = activeFilters.search.toLowerCase();
        const searchableText = `${job.title} ${job.department} ${job.location} ${job.description}`.toLowerCase();
        if (!searchableText.includes(searchTerm)) return false;
      }
      
      // Status filter
      if (activeFilters.status && job.status !== activeFilters.status) {
        return false;
      }
      
      // Priority filter
      if (activeFilters.priority && job.priority !== activeFilters.priority) {
        return false;
      }
      
      // Department filter
      if (activeFilters.department) {
        const deptTerm = activeFilters.department.toLowerCase();
        if (!job.department?.toLowerCase().includes(deptTerm)) return false;
      }
      
      // Employment type filter
      if (activeFilters.employment_type && job.employment_type !== activeFilters.employment_type) {
        return false;
      }
      
      // Experience level filter
      if (activeFilters.experience_level && job.experience_level !== activeFilters.experience_level) {
        return false;
      }
      
      // Location filter
      if (activeFilters.location) {
        const locTerm = activeFilters.location.toLowerCase();
        if (!job.location?.toLowerCase().includes(locTerm)) return false;
      }
      
      // Remote work filter
      if (activeFilters.remote_work_allowed) {
        const isRemoteAllowed = activeFilters.remote_work_allowed === 'true';
        if (job.remote_work_allowed !== isRemoteAllowed) return false;
      }
      
      // Salary filters
      if (activeFilters.salary_min) {
        const minSalary = parseInt(activeFilters.salary_min);
        if (!job.salary_range_max || job.salary_range_max < minSalary) return false;
      }
      
      if (activeFilters.salary_max) {
        const maxSalary = parseInt(activeFilters.salary_max);
        if (!job.salary_range_min || job.salary_range_min > maxSalary) return false;
      }
      
      // Date filters
      if (activeFilters.date_from) {
        const fromDate = new Date(activeFilters.date_from);
        const jobDate = new Date(job.created_at);
        if (jobDate < fromDate) return false;
      }
      
      if (activeFilters.date_to) {
        const toDate = new Date(activeFilters.date_to);
        const jobDate = new Date(job.created_at);
        if (jobDate > toDate) return false;
      }
      
      return true;
    });
  };

  const loadStats = async () => {
    try {
      const data = await jobService.getJobStats();
      setStats(data);
    } catch (error) {
      console.error('Error loading job stats:', error);
      // Set default stats on error
      setStats({
        total_jobs: 0,
        active_jobs: 0,
        draft_jobs: 0,
        closed_jobs: 0,
        urgent_priority: 0,
        applications_count: 0
      });
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handleSortChange = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const clearAllFilters = () => {
    setFilters({
      status: '',
      department: '',
      search: '',
      employment_type: '',
      experience_level: '',
      priority: '',
      location: '',
      remote_work_allowed: '',
      salary_min: '',
      salary_max: '',
      date_from: '',
      date_to: ''
    });
    setSearchInput('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Count active filters
  const activeFilterCount = Object.values(filters).filter(value => value && value.toString().trim() !== '').length;

  // Check if filters are active
  const hasActiveFilters = activeFilterCount > 0;

  const handleJobAction = async (jobId, action) => {
    try {
      let result;
      
      switch (action) {
        case 'publish':
          result = await jobService.publishJob(jobId);
          break;
        case 'close':
          result = await jobService.closeJob(jobId);
          break;
        default:
          console.error(`Unknown job action: ${action}`);
          return;
      }
      
      // Reload jobs and stats after successful action
      await Promise.all([loadJobs(), loadStats()]);
      
      // Could show success notification here
      console.log(`Job ${action} successful:`, result);
    } catch (error) {
      console.error(`Error ${action} job:`, error);
      // Could show error notification here
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (window.confirm('Are you sure you want to delete this job? This action cannot be undone.')) {
      try {
        await jobService.deleteJob(jobId);
        
        // Reload jobs and stats after successful deletion
        await Promise.all([loadJobs(), loadStats()]);
        
        // Could show success notification here
        console.log('Job deleted successfully');
      } catch (error) {
        console.error('Error deleting job:', error);
        // Could show error notification here
      }
    }
  };

  // Dialog handlers
  const handleViewJob = (jobId) => {
    setViewDialog({ isOpen: true, jobId });
  };

  const handleEditJob = (job) => {
    setEditDialog({ isOpen: true, job });
  };

  const handleDeleteJobDialog = (job) => {
    setDeleteDialog({ isOpen: true, job });
  };

  const handleJobUpdated = async (updatedJob) => {
    // Reload jobs and stats after successful update
    await Promise.all([loadJobs(), loadStats()]);
    console.log('Job updated successfully:', updatedJob);
  };

  const handleJobDeleted = async (jobId) => {
    // Reload jobs and stats after successful deletion
    await Promise.all([loadJobs(), loadStats()]);
    console.log('Job deleted successfully:', jobId);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'paused': return 'bg-blue-100 text-blue-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-blue-100 text-blue-800';
      case 'low': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!canViewJobs) {
    return (
      <div className="min-h-screen bg-background">
        <Header currentUser={currentUser} />
        <FixedSidebar currentPath="/job-management" />
        
        <main className="ml-72 pt-16">
          <div className="p-6">
            <div className="text-center py-12">
              <Icon name="Lock" size={48} className="mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">Access Denied</h3>
              <p className="text-muted-foreground mb-4">
                You don't have permission to view job postings.
              </p>
              <Button onClick={() => navigate('/hr-dashboard')} variant="outline">
                Return to Dashboard
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header currentUser={currentUser} />
      <FixedSidebar currentPath="/job-management" />
      
      <main className="ml-72 pt-16">
        <div className="p-6">
          <Breadcrumb customBreadcrumbs={[
            { label: 'Job Management', href: '/job-management', isActive: true }
          ]} />

          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Job Management</h1>
              <p className="text-muted-foreground">
                Create, manage, and track job postings across your organization
              </p>
            </div>
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              <Button variant="outline" onClick={() => loadJobs()}>
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Refresh
              </Button>
              
              {canCreateJobs && (
                <Button onClick={() => navigate('/job-management/create')}>
                  <Icon name="Plus" size={16} className="mr-2" />
                  Create Job
                </Button>
              )}
            </div>
          </div>

          {/* Job Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-6 mb-8">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Icon name="Briefcase" size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Jobs</p>
                  <p className="text-2xl font-bold text-foreground">{stats.total_jobs || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Icon name="CheckCircle" size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Active Jobs</p>
                  <p className="text-2xl font-bold text-foreground">{stats.active_jobs || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Icon name="Edit" size={20} className="text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Draft Jobs</p>
                  <p className="text-2xl font-bold text-foreground">{stats.draft_jobs || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <Icon name="AlertTriangle" size={20} className="text-red-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Urgent Priority</p>
                  <p className="text-2xl font-bold text-foreground">{stats.urgent_priority || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Icon name="Users" size={20} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Applications</p>
                  <p className="text-2xl font-bold text-foreground">{stats.applications_count || 0}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Debug Panel - Development Only */}
          {process.env.NODE_ENV === 'development' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <h4 className="text-sm font-semibold text-yellow-800 mb-2">🐛 Debug Info (Dev Only)</h4>
              <div className="text-xs text-yellow-700 space-y-1">
                <div><strong>Active Filters:</strong> {JSON.stringify(Object.fromEntries(Object.entries(filters).filter(([_, v]) => v)), null, 2)}</div>
                <div><strong>Total Jobs:</strong> {jobs.length} displayed, {pagination.total} total</div>
                <div><strong>Sort Config:</strong> {sortConfig.field} {sortConfig.direction}</div>
                <div><strong>Search Input:</strong> '{searchInput}'</div>
                <div><strong>Has Active Filters:</strong> {hasActiveFilters.toString()}</div>
              </div>
            </div>
          )}

          {/* Enhanced Filters */}
          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            {/* Filter Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <Icon name="Filter" size={20} className="text-muted-foreground" />
                <h3 className="text-lg font-semibold text-foreground">Filters</h3>
                {hasActiveFilters && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                    {activeFilterCount} active
                  </span>
                )}
              </div>
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearAllFilters}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Icon name="X" size={14} className="mr-2" />
                  Clear Filters
                </Button>
              )}
            </div>

            {/* Basic Filters Row */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Search</label>
                <div className="relative">
                  <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search jobs, titles, locations..."
                    value={searchInput}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="paused">Paused</option>
                  <option value="closed">Closed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Priority</label>
                <select
                  value={filters.priority}
                  onChange={(e) => handleFilterChange('priority', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">All Priorities</option>
                  <option value="urgent">Urgent</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Department</label>
                <input
                  type="text"
                  placeholder="Filter by department..."
                  value={filters.department}
                  onChange={(e) => handleFilterChange('department', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            {/* Advanced Filters Row */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Employment Type</label>
                <select
                  value={filters.employment_type}
                  onChange={(e) => handleFilterChange('employment_type', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">All Types</option>
                  <option value="full-time">Full-time</option>
                  <option value="part-time">Part-time</option>
                  <option value="contract">Contract</option>
                  <option value="internship">Internship</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Experience Level</label>
                <select
                  value={filters.experience_level}
                  onChange={(e) => handleFilterChange('experience_level', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">All Levels</option>
                  <option value="entry">Entry</option>
                  <option value="mid">Mid</option>
                  <option value="senior">Senior</option>
                  <option value="lead">Lead</option>
                  <option value="executive">Executive</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Location</label>
                <input
                  type="text"
                  placeholder="Filter by location..."
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Remote Work</label>
                <select
                  value={filters.remote_work_allowed}
                  onChange={(e) => handleFilterChange('remote_work_allowed', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">All Options</option>
                  <option value="true">Remote Allowed</option>
                  <option value="false">Office Only</option>
                </select>
              </div>
            </div>

            {/* Salary and Date Filters Row */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Min Salary</label>
                <input
                  type="number"
                  placeholder="Minimum salary"
                  value={filters.salary_min}
                  onChange={(e) => handleFilterChange('salary_min', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Max Salary</label>
                <input
                  type="number"
                  placeholder="Maximum salary"
                  value={filters.salary_max}
                  onChange={(e) => handleFilterChange('salary_max', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Created From</label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => handleFilterChange('date_from', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Created To</label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => handleFilterChange('date_to', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
          </div>

          {/* Jobs Table */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">
                Jobs ({pagination.total})
              </h3>
            </div>
            
            {loading ? (
              <div className="text-center py-12">
                <Icon name="Loader2" size={48} className="mx-auto text-muted-foreground mb-4 animate-spin" />
                <p className="text-muted-foreground">Loading jobs...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-6 py-3 text-left">
                        <button
                          onClick={() => handleSortChange('title')}
                          className="flex items-center space-x-1 text-xs font-medium text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
                        >
                          <span>Job Details</span>
                          {sortConfig.field === 'title' && (
                            <Icon
                              name={sortConfig.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                              size={14}
                            />
                          )}
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left">
                        <button
                          onClick={() => handleSortChange('department')}
                          className="flex items-center space-x-1 text-xs font-medium text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
                        >
                          <span>Department</span>
                          {sortConfig.field === 'department' && (
                            <Icon
                              name={sortConfig.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                              size={14}
                            />
                          )}
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left">
                        <button
                          onClick={() => handleSortChange('status')}
                          className="flex items-center space-x-1 text-xs font-medium text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
                        >
                          <span>Status</span>
                          {sortConfig.field === 'status' && (
                            <Icon
                              name={sortConfig.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                              size={14}
                            />
                          )}
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left">
                        <button
                          onClick={() => handleSortChange('priority')}
                          className="flex items-center space-x-1 text-xs font-medium text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
                        >
                          <span>Priority</span>
                          {sortConfig.field === 'priority' && (
                            <Icon
                              name={sortConfig.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                              size={14}
                            />
                          )}
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left">
                        <button
                          onClick={() => handleSortChange('salary_range_min')}
                          className="flex items-center space-x-1 text-xs font-medium text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
                        >
                          <span>Salary</span>
                          {sortConfig.field === 'salary_range_min' && (
                            <Icon
                              name={sortConfig.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                              size={14}
                            />
                          )}
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left">
                        <button
                          onClick={() => handleSortChange('created_at')}
                          className="flex items-center space-x-1 text-xs font-medium text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
                        >
                          <span>Created</span>
                          {sortConfig.field === 'created_at' && (
                            <Icon
                              name={sortConfig.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                              size={14}
                            />
                          )}
                        </button>
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {jobs.map((job) => (
                      <tr key={job.id} className="hover:bg-muted/25">
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-foreground">{job.title}</div>
                            <div className="text-sm text-muted-foreground">
                              {job.location} • {job.employment_type} • {job.experience_level}
                            </div>
                            {job.remote_work_allowed && (
                              <div className="inline-flex items-center mt-1">
                                <Icon name="Wifi" size={12} className="mr-1 text-green-600" />
                                <span className="text-xs text-green-600">Remote friendly</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-foreground">{job.department}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(job.status)}`}>
                            {job.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(job.priority)}`}>
                            {job.priority}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-foreground">
                            {formatSalary(job.salary_range_min, job.salary_range_max, job.currency)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          {new Date(job.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewJob(job.id)}
                            title="View Job Details"
                          >
                            <Icon name="Eye" size={14} />
                          </Button>
                          
                          {canManageJobs && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditJob(job)}
                                title="Edit Job"
                              >
                                <Icon name="Edit" size={14} />
                              </Button>
                              
                              {job.status === 'draft' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleJobAction(job.id, 'publish')}
                                  className="text-green-600 hover:text-green-700"
                                  title="Publish Job"
                                >
                                  <Icon name="Send" size={14} />
                                </Button>
                              )}
                              
                              {job.status === 'active' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleJobAction(job.id, 'close')}
                                  className="text-orange-600 hover:text-orange-700"
                                  title="Close Job"
                                >
                                  <Icon name="Pause" size={14} />
                                </Button>
                              )}
                            </>
                          )}
                          
                          {canDeleteJobs && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteJobDialog(job)}
                              className="text-red-600 hover:text-red-700"
                              title="Delete Job"
                            >
                              <Icon name="Trash2" size={14} />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {jobs.length === 0 && !loading && (
              <div className="text-center py-12">
                <Icon name="Briefcase" size={48} className="mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">No jobs found</h3>
                <p className="text-muted-foreground mb-4">
                  {hasActiveFilters ? (
                    <>
                      No jobs match your current filters.
                      <button
                        onClick={clearAllFilters}
                        className="ml-2 text-primary hover:text-primary/80 underline"
                      >
                        Clear all filters
                      </button>
                      to see all jobs.
                    </>
                  ) : (
                    'Create your first job posting to get started'
                  )}
                </p>
                {!hasActiveFilters && canCreateJobs && (
                  <Button onClick={() => navigate('/job-management/create')}>
                    <Icon name="Plus" size={16} className="mr-2" />
                    Create Job
                  </Button>
                )}
              </div>
            )}

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="px-6 py-4 border-t border-border">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Page {pagination.page} of {pagination.total_pages} ({pagination.total} total)
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={pagination.page === 1}
                    >
                      <Icon name="ChevronLeft" size={16} />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={pagination.page === pagination.total_pages}
                    >
                      <Icon name="ChevronRight" size={16} />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      
      {/* Dialogs */}
      <ViewJobDialog
        isOpen={viewDialog.isOpen}
        onClose={() => setViewDialog({ isOpen: false, jobId: null })}
        jobId={viewDialog.jobId}
        onEdit={handleEditJob}
        onDelete={handleDeleteJobDialog}
        userRole={currentUser?.role}
      />
      
      <EditJobDialog
        isOpen={editDialog.isOpen}
        onClose={() => setEditDialog({ isOpen: false, job: null })}
        job={editDialog.job}
        onJobUpdated={handleJobUpdated}
      />
      
      <DeleteJobDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, job: null })}
        job={deleteDialog.job}
        onJobDeleted={handleJobDeleted}
      />
    </div>
  );
};

export default JobManagementPage;